import React, { useState } from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import { conditionField, operator } from '../config/rateShopConstants';
import { US_STATES, US_STATE_NAMES } from '../config/ruleEditorCopy';
import StoreChecklist from './StoreChecklist';
import ValuePicker from './ValuePicker';

const DestinationChoice = ({ choices, value, onChange, disabled, label }) => {
  const [choosingOther, setChoosingOther] = useState(false);
  const other = value ? !choices.some(c => c.value === value) : choosingOther;
  return (
    <div>
      <Form.Select
        aria-label={label}
        disabled={disabled}
        value={other ? '__other' : value || ''}
        onChange={e => {
          const custom = e.target.value === '__other';
          setChoosingOther(custom);
          onChange(custom ? '' : e.target.value);
        }}
      >
        <option value="">Choose…</option>
        {choices.map(c => (
          <option key={c.value} value={c.value}>
            {c.label}
          </option>
        ))}
        <option value="__other">Other — enter a value</option>
      </Form.Select>
      {other && (
        <Form.Control
          className="mt-2"
          aria-label={`Other ${label}`}
          placeholder="Enter country or province code"
          disabled={disabled}
          value={value}
          onChange={e => onChange(e.target.value)}
        />
      )}
    </div>
  );
};
const countries = [
  { value: 'US', label: 'United States (US)' },
  { value: 'CA', label: 'Canada (CA)' },
  { value: 'MX', label: 'Mexico (MX)' }
];
const ConditionValue = ({
  condition,
  onChange,
  disabled,
  tagNames,
  tagsLoading,
  stores
}) => {
  const field = conditionField(condition.field);
  const shape = operator(condition.op)?.value;
  const many = shape === 'many';
  if (shape === 'range')
    return (
      <InputGroup>
        {[0, 1].map(i => (
          <React.Fragment key={i}>
            {i === 1 && <InputGroup.Text>and</InputGroup.Text>}
            <Form.Control
              aria-label={i ? 'Maximum' : 'Minimum'}
              type="number"
              step="any"
              disabled={disabled}
              value={condition.value?.[i] ?? ''}
              onChange={e => {
                const next = [
                  ...(Array.isArray(condition.value)
                    ? condition.value
                    : ['', ''])
                ];
                next[i] = e.target.value === '' ? '' : Number(e.target.value);
                onChange(next);
              }}
            />
          </React.Fragment>
        ))}
        {field?.unit && <InputGroup.Text>{field.unit}</InputGroup.Text>}
      </InputGroup>
    );
  if (field?.type === 'bool')
    return (
      <Form.Select
        aria-label="Residential yes or no"
        disabled={disabled}
        value={String(
          condition.op === 'neq' ? !condition.value : Boolean(condition.value)
        )}
        onChange={e =>
          onChange(
            condition.op === 'neq'
              ? e.target.value !== 'true'
              : e.target.value === 'true'
          )
        }
      >
        <option value="true">Yes</option>
        <option value="false">No</option>
      </Form.Select>
    );
  if (field?.input === 'store')
    return (
      <details>
        <summary className="small text-primary">
          Choose {many ? 'stores' : 'a store'} (
          {Array.isArray(condition.value)
            ? condition.value.length
            : condition.value
            ? 1
            : 0}{' '}
          selected)
        </summary>
        <StoreChecklist
          stores={stores}
          value={condition.value}
          multiple={many}
          disabled={disabled}
          onChange={onChange}
        />
      </details>
    );
  if (field?.input === 'tag')
    return (
      <ValuePicker
        label="ShipStation tags"
        multiple={many}
        creatable
        value={condition.value}
        disabled={disabled}
        loading={tagsLoading}
        onChange={onChange}
        options={tagNames.map(t => ({ value: t, label: t }))}
      />
    );
  if (many && ['countryCode', 'state'].includes(condition.field))
    return (
      <ValuePicker
        label={
          condition.field === 'state' ? 'States or provinces' : 'Countries'
        }
        multiple
        creatable
        value={condition.value}
        disabled={disabled}
        onChange={onChange}
        options={
          condition.field === 'countryCode'
            ? countries
            : US_STATES.map((value, i) => ({
                value,
                label: `${US_STATE_NAMES[i]} (${value})`
              }))
        }
      />
    );
  if (many)
    return (
      <div>
        <ValuePicker
          label="Values to match"
          multiple
          creatable
          value={condition.value}
          disabled={disabled}
          isValidNewOption={
            field?.type === 'number'
              ? input => input.trim() !== '' && Number.isFinite(Number(input))
              : undefined
          }
          onChange={values =>
            onChange(field?.type === 'number' ? values.map(Number) : values)
          }
        />
        {field?.unit && (
          <span className="small text-muted">Values in {field.unit}</span>
        )}
      </div>
    );
  if (condition.field === 'countryCode')
    return (
      <DestinationChoice
        key="country"
        label="Country"
        choices={countries}
        value={condition.value}
        onChange={onChange}
        disabled={disabled}
      />
    );
  if (condition.field === 'state')
    return (
      <DestinationChoice
        key="state"
        label="State or province"
        choices={US_STATES.map((value, i) => ({
          value,
          label: `${US_STATE_NAMES[i]} (${value})`
        }))}
        value={condition.value}
        onChange={onChange}
        disabled={disabled}
      />
    );
  return (
    <InputGroup>
      {field?.unit === '$' && <InputGroup.Text>$</InputGroup.Text>}
      <Form.Control
        aria-label="Value to match"
        type={field?.type === 'number' ? 'number' : 'text'}
        step="any"
        disabled={disabled}
        value={condition.value ?? ''}
        placeholder={field?.placeholder || 'Enter a value'}
        onChange={e =>
          onChange(
            field?.type === 'number' && e.target.value !== ''
              ? Number(e.target.value)
              : e.target.value
          )
        }
      />
      {field?.unit && field.unit !== '$' && (
        <InputGroup.Text>{field.unit}</InputGroup.Text>
      )}
    </InputGroup>
  );
};
export default ConditionValue;
